import { Link } from 'react-router-dom';
import {
  CpuChipIcon,
  WrenchScrewdriverIcon,
  ServerStackIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Agent } from '../../types';
import {
  useAgentMcpConnections,
  useRemoveAgentMcpConnection,
} from '../../hooks/useAgents';
import { useModelConfigs } from '../../hooks/useModelConfigs';
import { ModelConfigPickerOptions, ModelLifecycleNote, ModelPolicyNote } from '../modelconfigs/ModelConfigPickerOptions';
import { FailoverModelConfigPicker } from '../modelconfigs/FailoverModelConfigPicker';
import { useAccountModelPicks } from '../../hooks/useAccountModels';
import { useState } from 'react';
import { useFlag } from '../../hooks/useFeatureFlags';

// Available tools that agents can use during collaboration execution
const AVAILABLE_TOOLS = [
  { value: 'file.read', label: 'File Read', description: 'Read files from data directory' },
  { value: 'file.write', label: 'File Write', description: 'Write files to data directory' },
  { value: 'http.fetch', label: 'HTTP Fetch', description: 'Fetch data from HTTP APIs' },
  { value: 'create.getProject', label: 'Get Project', description: 'Get project from Create API' },
  { value: 'create.listProjects', label: 'List Projects', description: 'List projects from Create API' },
];

interface EditableCompactConfigProps {
  agent: Agent;
  isEditing?: boolean;
  onFieldChange?: (field: string, value: any) => void;
  onAddMcp?: () => void;
}

export function EditableCompactConfig({
  agent,
  isEditing = false,
  onFieldChange,
  onAddMcp,
}: EditableCompactConfigProps) {
  const { data: mcpConnections } = useAgentMcpConnections(agent.id);
  const { data: modelConfigsData, isLoading: modelConfigsLoading } = useModelConfigs(1, 100);
  // The account's model policy (task 1147): disallowed configs grey out, the default is marked.
  const modelPolicy = useAccountModelPicks();
  const modelConfigs = modelConfigsData?.data || [];
  const removeMcpConnection = useRemoveAgentMcpConnection();
  const [removingMcpId, setRemovingMcpId] = useState<string | null>(null);
  // Picking a catalog model is open to everyone; adding one to the shared
  // catalog is Monarch staff work (task 569).
  const canManageCatalog = useFlag('innovate.fe.model-configs.create');

  const handleRemoveMcp = async (mcpConnectionId: string) => {
    if (removingMcpId === mcpConnectionId) {
      try {
        await removeMcpConnection.mutateAsync({ agentId: agent.id, mcpConnectionId });
        setRemovingMcpId(null);
      } catch (error) {
        console.error('Failed to remove MCP connection:', error);
      }
    } else {
      setRemovingMcpId(mcpConnectionId);
    }
  };

  const handleToolToggle = (toolValue: string, checked: boolean) => {
    const currentTools = agent.tools || [];
    const newTools = checked
      ? [...currentTools, toolValue]
      : currentTools.filter((t) => t !== toolValue);
    onFieldChange?.('tools', newTools);
  };

  // View mode - same as original CompactConfig
  if (!isEditing) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
          {/* Model Config */}
          <div className="flex items-center gap-2">
            <CpuChipIcon className="h-4 w-4 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">Model:</span>
            {agent.modelConfig ? (
              <Link
                to={`/model-configs/${agent.modelConfigId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
              >
                {agent.modelConfig.name || agent.modelConfig.model.modelId} ({agent.modelConfig.model.provider?.key ?? 'unknown'})
              </Link>
            ) : (
              <span className="font-medium text-gray-900 dark:text-white">None</span>
            )}
          </div>

          {/* Tools */}
          <div className="flex items-center gap-2">
            <WrenchScrewdriverIcon className="h-4 w-4 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">Tools:</span>
            {agent.tools && agent.tools.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {agent.tools.map((tool) => (
                  <span
                    key={tool}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-gray-400 dark:text-gray-500 italic">None</span>
            )}
          </div>

          {/* MCP Connections */}
          <div className="flex items-center gap-2">
            <ServerStackIcon className="h-4 w-4 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">MCP:</span>
            {mcpConnections && mcpConnections.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {mcpConnections.map((amc) => (
                  <span
                    key={amc.id}
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      amc.mcpConnection?.isActive
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                  >
                    {amc.mcpConnection?.name || 'Unknown'}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-gray-400 dark:text-gray-500 italic">None</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Edit mode
  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4">
      <h3 className="text-sm font-medium text-gray-900 dark:text-white">Configuration</h3>

      {/* Model Config */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <CpuChipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-500 dark:text-gray-400">Model:</span>
        </div>
        <div className="flex-1 flex items-center gap-2">
          <select
            value={agent.modelConfigId || ''}
            onChange={(e) => {
              onFieldChange?.('modelConfigId', e.target.value || null);
              // The service refuses the same config as primary and failover.
              if (e.target.value && e.target.value === agent.failoverModelConfigId) {
                onFieldChange?.('failoverModelConfigId', null);
              }
            }}
            disabled={modelConfigsLoading}
            className="flex-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
          >
            <option value="">No default config</option>
            <ModelConfigPickerOptions
              configs={modelConfigs}
              selectedId={agent.modelConfigId}
              policy={modelPolicy}
              label={(config) => `${config.name || config.model.modelId} (${config.model.provider?.key ?? 'unknown'})`}
            />
          </select>
          {canManageCatalog && (
            <Link
              to="/model-configs/new"
              className="inline-flex items-center px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Create new Model config"
            >
              <PlusIcon className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>
      <ModelLifecycleNote configs={modelConfigs} selectedId={agent.modelConfigId} />
      <ModelPolicyNote configs={modelConfigs} selectedId={agent.modelConfigId} policy={modelPolicy} />

      {/* Failover Model Config (task 1301) */}
      <FailoverModelConfigPicker
        configs={modelConfigs}
        policy={modelPolicy}
        primaryId={agent.modelConfigId}
        value={agent.failoverModelConfigId}
        onChange={(id) => onFieldChange?.('failoverModelConfigId', id)}
        disabled={modelConfigsLoading}
      />

      {/* Tools */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <WrenchScrewdriverIcon className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-500 dark:text-gray-400">Tools:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {AVAILABLE_TOOLS.map((tool) => (
            <label
              key={tool.value}
              className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm cursor-pointer border transition-colors ${
                (agent.tools || []).includes(tool.value)
                  ? 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900 dark:text-indigo-200 dark:border-indigo-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
              }`}
              title={tool.description}
            >
              <input
                type="checkbox"
                checked={(agent.tools || []).includes(tool.value)}
                onChange={(e) => handleToolToggle(tool.value, e.target.checked)}
                className="sr-only"
              />
              {tool.label}
            </label>
          ))}
        </div>
      </div>

      {/* MCP Connections */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <ServerStackIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-500 dark:text-gray-400">MCP Connections:</span>
          </div>
          <button
            type="button"
            onClick={onAddMcp}
            className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add
          </button>
        </div>
        {!mcpConnections || mcpConnections.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">
            No MCP connections linked.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {mcpConnections.map((amc) => (
              <span
                key={amc.id}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm ${
                  amc.mcpConnection?.isActive
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}
              >
                {amc.mcpConnection?.name || 'Unknown'}
                {removingMcpId === amc.mcpConnectionId ? (
                  <>
                    <button
                      type="button"
                      onClick={() => handleRemoveMcp(amc.mcpConnectionId)}
                      disabled={removeMcpConnection.isPending}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 disabled:opacity-50 ml-1"
                    >
                      {removeMcpConnection.isPending ? '...' : 'Yes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setRemovingMcpId(null)}
                      className="text-gray-500 dark:text-gray-400"
                    >
                      No
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleRemoveMcp(amc.mcpConnectionId)}
                    className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    title="Remove"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
